import { api } from '../../api/client';
import type {
  ReagentPrepComponent, ReagentPrepDetail, ReagentPrepInput, ReagentLookupOption,
  DepletedRow, LltDepletionRow, LltSearchRow, ReagentDateFilter, ReagentPrepListRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation — API calls for the three list screens.
// Backend: `Api/Controllers/V1/ReagentPrep/ReagentPrepLogController.cs`.
// The shared axios client injects the JWT and drives the global loader, so no
// page here handles auth headers or spinners (§18.6).
// ─────────────────────────────────────────────────────────────────────────────

/** Every list response carries the filter the SERVER applied, so a page can
 *  select the right option on first load instead of guessing the tenant default. */
interface ListEnvelope<T> {
  success: boolean;
  data: T[];
  meta?: { dateFilter?: ReagentDateFilter };
}

export interface ListResult<T> { rows: T[]; dateFilter: ReagentDateFilter }

/**
 * Omitting `dateFilter` asks the server for the tenant default from
 * Settings → Default Settings → "Reagent Preparation" (spec §7) — which is why
 * the applied filter comes back in `meta` rather than being assumed here.
 */
async function getList<T>(path: string, dateFilter?: ReagentDateFilter): Promise<ListResult<T>> {
  const res = await api.get<ListEnvelope<T>>(path, {
    params: dateFilter ? { dateFilter } : undefined,
  });
  return {
    rows: res.data.data ?? [],
    dateFilter: res.data.meta?.dateFilter ?? dateFilter ?? 'All',
  };
}

/** Reagent Prep Log list (spec §7) — the module's landing view. */
export const fetchReagentPreps = (dateFilter?: ReagentDateFilter) =>
  getList<ReagentPrepListRow>('/api/v1/reagent-preps', dateFilter);

/** LLT Search (spec §16.1) — read-only. */
export const fetchLltSearch = (dateFilter?: ReagentDateFilter) =>
  getList<LltSearchRow>('/api/v1/reagent-preps/llt-search', dateFilter);

/** LLT Depletion list (spec §16.2) — undepleted, non-Calibration preps. */
export const fetchLltDepletion = (dateFilter?: ReagentDateFilter) =>
  getList<LltDepletionRow>('/api/v1/reagent-preps/depletion', dateFilter);

export interface DepleteResult {
  /** Rows this call actually depleted. Fewer than `requested` when some ids
   *  were already depleted — that is a success, not a partial failure. */
  depleted: number;
  requested: number;
  rows: DepletedRow[];
}

/**
 * LLT Depletion → Save (spec §16.2).  Sends ONLY the ids: the server stamps the
 * depletion date and user, so the browser can never commit a half-bound row.
 * Idempotent — re-posting the same ids returns `depleted: 0`.
 */
export async function depleteLlts(ids: string[]): Promise<DepleteResult> {
  const res = await api.post<{ success: boolean; data: DepleteResult }>(
    '/api/v1/reagent-preps/depletion', { ids },
  );
  return res.data.data;
}

// ── Detail views: the XAF Save / Delete / New / Copy / Ok actions ───────────

/** One header for the Chemistry / Micro Media / Calibration detail view. */
export async function fetchReagentPrep(id: string): Promise<ReagentPrepDetail> {
  const res = await api.get<{ data: ReagentPrepDetail }>(`/api/v1/reagent-preps/${id}`);
  return res.data.data;
}

/** The Components grid (Chemistry / Micro) or Levels grid (Calibration). */
export async function fetchComponents(id: string): Promise<ReagentPrepComponent[]> {
  const res = await api.get<{ data: ReagentPrepComponent[] }>(`/api/v1/reagent-preps/${id}/components`);
  return res.data.data ?? [];
}

/** Dropdown feed. `kind` is whitelisted server-side — an unknown one is a 400. */
export async function fetchLookup(kind: string): Promise<ReagentLookupOption[]> {
  const res = await api.get<{ data: ReagentLookupOption[] }>(`/api/v1/reagent-preps/lookups/${kind}`);
  return res.data.data ?? [];
}

export interface SaveResult { oid: string; llt: string | null }

/** Create — mints the LLT race-safely server-side (spec §15). */
export async function createReagentPrep(input: ReagentPrepInput): Promise<SaveResult> {
  const res = await api.post<{ data: SaveResult }>('/api/v1/reagent-preps', input);
  return res.data.data;
}

/** Save an existing preparation. Audits one row per CHANGED field, by name. */
export async function updateReagentPrep(id: string, input: ReagentPrepInput): Promise<SaveResult> {
  const res = await api.put<{ data: SaveResult }>(`/api/v1/reagent-preps/${id}`, input);
  return res.data.data;
}

/** Delete — soft-deletes each header AND cascades to its components (§4.1).
 *  POST rather than DELETE because it takes a SET of ids; a DELETE with a body
 *  is not reliably supported across proxies. */
export async function deleteReagentPreps(ids: string[]): Promise<{ deleted: number; requested: number }> {
  const res = await api.post<{ data: { deleted: number; requested: number } }>(
    '/api/v1/reagent-preps/delete', { ids },
  );
  return res.data.data;
}

/** Classify → Copy Previous (spec §8 tab 2): clone under a fresh LLT. */
export async function copyReagentPrep(sourceId: string): Promise<SaveResult> {
  const res = await api.post<{ data: SaveResult }>(`/api/v1/reagent-preps/${sourceId}/copy`, {});
  return res.data.data;
}

/** Calibration → the [Ok] button (spec §12.2): generate the levels. */
export async function generateLevels(id: string): Promise<number> {
  const res = await api.post<{ data: { levelCount: number } }>(`/api/v1/reagent-preps/${id}/levels`, {});
  return res.data.data.levelCount;
}

/** Components grid → COA "Attachment" (XAF PreviewCOA_Reagent): the component's
 *  LT vendor certificate or LLT notebook. Null when there is nothing to show. */
export async function fetchComponentCoa(prepId: string, componentOid: string): Promise<Blob | null> {
  const res = await api.get(`/api/v1/reagent-preps/${prepId}/components/${componentOid}/coa`, { responseType: 'blob' });
  const blob = res.data as Blob;
  return res.status === 204 || !blob || blob.size === 0 ? null : blob;
}

/** One edited Calibration level (mirrors the API's CalibrationLevelInput). */
export interface CalibrationLevelDto {
  oid: string;
  workingStdName: string | null;
  stockStdName: string | null;
  cal_Weight_g_w1: string | null;    calWsUnitsId: string | null;
  cal_VolumeTaken_V1: string | null; calV1UnitsId: string | null;
  cal_FinalVol_V2: string | null;    calV2UnitsId: string | null;
  cal_FinalConc_C2: string | null;   calC2UnitsId: string | null;
  formula: string | null;
  comment: string | null;
}

/** Calibration levels grid → stored with the header's Save (XAF batch edit).
 *  The SERVER recomputes Formula and FinalConc from WSconc / VolTaken / FinalVol. */
export async function saveLevels(id: string, levels: CalibrationLevelDto[]): Promise<number> {
  const res = await api.put<{ data: { levelCount: number } }>(
    `/api/v1/reagent-preps/${id}/levels`, { levels },
  );
  return res.data.data.levelCount;
}

/** The StockStdName picker's rows. The levels are sent because XAF builds the
 *  list from the grid's UNSAVED edits. */
export async function fetchStockStdOptions(id: string, body: {
  levelOid: string;
  selectStockSolution: number | null;
  vendorStockId: string | null;
  labStockId: string | null;
  levels: Array<{ oid: string; componentID: number; workingStdName: string | null }>;
}): Promise<string[]> {
  const res = await api.post<{ data: string[] }>(
    `/api/v1/reagent-preps/${id}/levels/stock-std-options`, body,
  );
  return res.data.data ?? [];
}

/** Prep Notepad → OK (spec §9.2): write the component buffer. Upserted on
 *  ComponentID; a component no longer in the buffer is left alone, because the
 *  Notepad has no delete. */
export async function saveComponents(
  id: string, components: PrepNotepadComponentDto[],
): Promise<number> {
  const res = await api.post<{ data: { componentCount: number } }>(
    `/api/v1/reagent-preps/${id}/components`, { components },
  );
  return res.data.data.componentCount;
}

/** Mirrors the API PrepNotepadComponent. FKs are OIDs taken straight from the
 *  grid-lookup rows — item and standard names repeat in live data, so a name
 *  round-trip could attach the wrong lot. LTExpDate / LLTExpDate are absent on
 *  purpose: they are derived from the chosen lot server-side (spec §4.2). */
export interface PrepNotepadComponentDto {
  componentID: number;
  vendorStockId: string | null;
  ltId: string | null;
  labStockId: string | null;
  lltId: string | null;
  solvent: boolean;
  calculationApproachId: string | null;
  w1UnitsId: string | null;
  v1UnitsId: string | null;
  c1UnitsId: string | null;
  v2UnitsId: string | null;
  c2UnitsId: string | null;
  stockConc_C1: string | null;
  volumeTaken_V1: string | null;
  weight_g_w1: string | null;
  finalVol_V2: string | null;
  finalConc_C2: string | null;
  purity: string | null;
  density: string | null;
  mw: string | null;
  eqWt: string | null;
  constant: string | null;
  formula: string | null;
  comment: string | null;
  phCriteria: string | null;
  ph: string | null;
  autoclave: string | null;
  filterSterilization: string | null;
  sporeGrowth: string | null;
  positiveControl: string | null;
  negativeControl: string | null;
}

// ── Calculation engine (spec §10.3) ─────────────────────────────────────────

/** How one measurement field should behave, as the server decides it. */
export type ReagentFieldState = 'Input' | 'Result' | 'NotApplicable';

/** The engine's answer for the component being edited. */
export interface ReagentCalcResult {
  matched: boolean;
  codeId: string | null;
  formula: string | null;
  /** The token the formula writes — 'V1' | 'W1' | 'C1' | 'V2' | 'C2'. */
  target: string | null;
  /** Already rounded to 3 decimals server-side. */
  value: number | null;
  fieldStates: Record<string, ReagentFieldState>;
  /** Why nothing was computed yet — a hint, never an error. */
  pending: string | null;
}

/** What the Notepad sends. Measurements stay STRINGS: the legacy columns are
 *  nvarchar and re-typing an analyst's entry on the way out would change it. */
export interface ReagentCalcRequest {
  calculationApproachId: string | null;
  v1UnitsId: string | null;
  w1UnitsId: string | null;
  c1UnitsId: string | null;
  v2UnitsId: string | null;
  c2UnitsId: string | null;
  volumeTaken_V1: string | null;
  weight_g_w1: string | null;
  stockConc_C1: string | null;
  finalVol_V2: string | null;
  finalConc_C2: string | null;
  purity: string | null;
  density: string | null;
  mw: string | null;
  eqWt: string | null;
  constant: string | null;
}

/**
 * Runs the reagent-prep calculation SERVER-SIDE and returns the result plus the
 * field states. Read-only, so it is safe to call as the analyst types.
 *
 * The arithmetic deliberately does NOT live in this file: the API owns it
 * (CLAUDE.md §1), so a bench number is the same whatever client produced it.
 */
export async function calculateComponent(body: ReagentCalcRequest): Promise<ReagentCalcResult> {
  // `silentLoading` keeps the global progress bar out of it — this runs while
  // the analyst types, and a bar flickering on every pause would read as the
  // form struggling. The `as never` cast is how the rest of the app passes it
  // (the flag is a client extension, not an axios property).
  const res = await api.post<{ success: boolean; data: ReagentCalcResult }>(
    '/api/v1/reagent-preps/calculate', body, { silentLoading: true } as never,
  );
  return res.data.data;
}
